import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import { ContentUpgrades } from "@swizec/gatsby-theme-course-platform";
export const _frontmatter = {
  "title": "Why senior engineers get nothing done",
  "description": "You start a new job and it's amazing. Code all day, clear objectives, easy guidelines, ship a bunch of features be a hero. Then something happens and suddenly you get nothing done. 🤔",
  "categories": "Opinions, Learning, Mindset",
  "content_upgrade": "SeniorMindset",
  "published": "2020-09-11T00:00:00.000Z",
  "hero": "./img/screenshot-1599840085921.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <p>{`Remember when you started your job, how was it?`}</p>
    <p>{`Let me guess: the sheer amount of new things to learn was overwhelming, you felt out of your depth, and your calendar was open and free after the first week of onboarding activities.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/ceLygpJHjm3te/giphy-loop.mp4?cid=7cf28b0f2vygml5hjozk2jt18jg8h0ci2pf9mcy49gd2q2so&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Best part of starting a new job my friend 👉 You get to work. It's wonderful.`}</p>
    <h2 {...{
      "id": "when-youre-new-life-is-great"
    }}>{`When you're new life is great`}</h2>
    <p>{`Imagine a typical workday for a new team member:`}</p>
    <ul>
      <li parentName="ul">{`10:00am – standup`}</li>
      <li parentName="ul">{`10:15am – work on tickets`}</li>
      <li parentName="ul">{`11:00am – quick chat to ask a question`}</li>
      <li parentName="ul">{`11:15am - work on tickets`}</li>
      <li parentName="ul">{`12:00am – lunch`}</li>
      <li parentName="ul">{`12:30am – work on tickets`}</li>
      <li parentName="ul">{`15:00pm - review PRs`}</li>
      <li parentName="ul">{`15:30pm - break`}</li>
      <li parentName="ul">{`16:00pm - work on tickets`}</li>
      <li parentName="ul">{`18:00pm - done for the day`}</li>
    </ul>
    <p>{`8 hour block of time, 30 minutes of meetings, 30 minutes of PRs, a whole lotta coding. 😍`}</p>
    <p>{`A good manager will put you on `}<strong parentName="p">{`one long project`}</strong>{`. It's the best way to learn.`}</p>
    <p>{`Get a big project and do it. You'll explore the system, figure out how pieces move together, and have a common thread to guide you.`}</p>
    <p>{`New junior engineers get to work on parts of the same big project over weeks and months. New senior engineers do the whole thing.`}</p>
    <p>{`Both are valid.`}</p>
    <ContentUpgrades.SeniorMindset />
    <h2 {...{
      "id": "when-youre-seasoned-though"
    }}>{`When you're seasoned though`}</h2>
    <p>{`Now imagine what a typical day looks like for a seasoned engineer my friend.`}</p>
    <ul>
      <li parentName="ul">{`10:00am - standup`}</li>
      <li parentName="ul">{`10:15am - unblock Susan`}</li>
      <li parentName="ul">{`10:30am - meet with product manager`}</li>
      <li parentName="ul">{`11:00am - quick chat to answer Bob's question`}</li>
      <li parentName="ul">{`11:15am - answer PM's followup question`}</li>
      <li parentName="ul">{`11:30am - code reviews`}</li>
      <li parentName="ul">{`12:00am - lunch`}</li>
      <li parentName="ul">{`12:30am - 1-on-1 to welcome new team member to team`}</li>
      <li parentName="ul">{`13:00pm - 5 slack threads of questions`}</li>
      <li parentName="ul">{`13:30pm - production bug`}</li>
      <li parentName="ul">{`13:45pm - unblock Joe`}</li>
      <li parentName="ul">{`14:00pm - meet with head of engineering`}</li>
      <li parentName="ul">{`14:30pm - write tech specs for next month's project`}</li>
      <li parentName="ul">{`15:00pm - quick chat with PM to clarify something`}</li>
      <li parentName="ul">{`15:15pm - continue writing specs`}</li>
      <li parentName="ul">{`15:45pm - unblock Alice`}</li>
      <li parentName="ul">{`16:00pm - work on tickets`}</li>
      <li parentName="ul">{`16:15pm - notification in #bugs channel`}</li>
      <li parentName="ul">{`16:20pm - work on tickets`}</li>
      <li parentName="ul">{`17:50pm - catch up on Slack threads`}</li>
      <li parentName="ul">{`18:15pm - done for the day`}</li>
    </ul>
    <p>{`8 hour block of time, 2h45min of meetings, 45min of writing tech specs, and an hour of coding. Never more than 30min of focus time. 😅`}</p>
    <p>{`And that's why senior engineers get nothing done. They're too experienced and know too much.`}</p>
    <h2 {...{
      "id": "how-this-happens-to-you"
    }}>{`How this happens to you`}</h2>
    <p>{`You start with writing code and delivering fantastic results. You're killing it and everybody loves you! Rock on.`}</p>
    <p>{`Then your code hits production.`}</p>
    <p>{`All code has bugs, yours too my friend, I promise.`}</p>
    <p><figure parentName="p" {...{
        "className": "gatsby-resp-image-figure",
        "style": {
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
    `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "639px"
          }
        }}>{`
      `}<a parentName="span" {...{
            "className": "gatsby-resp-image-link",
            "href": "/static/4be3c5f057137a44f2c170597e69be2c/df7e7/Grumpy-cat-with-bugsica469.jpg",
            "style": {
              "display": "block"
            },
            "target": "_blank",
            "rel": "noopener"
          }}>{`
    `}<span parentName="a" {...{
              "className": "gatsby-resp-image-background-image",
              "style": {
                "paddingBottom": "50.224215246636774%",
                "position": "relative",
                "bottom": "0",
                "left": "0",
                "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'200\\'%20viewBox=\\'0%200%20400%20200\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M55%2031a55%2055%200%20002%2027c1%203%201%203%203%202%201-2%202-2%203-1%200%201%201%201%201-1%202-3%205-6%206-5l2-1%203-4c2%200%201-1-9-11L56%2027l-1%204m80%208l-7%205-6%203-3%201v6c0%202%201%203%203%203s3%203%201%205l2%203%202%204-4-2-3-3-3-2-3-1h-4l-3%201c-3%203-3%203-3%201s0-1-1%201v5c0%203-3%208-5%208l-1-6c0-5%200-6-3-6l-3-3c0-2-1-2-4-2l-3-1-4-1-5-1h-2c0%201-1%202-4%201-2%200-3%200-2%201v1c-6%200-9%2016-4%2020l2%203%204%203%2014%205%202-2c0-2%202-3%203-3l5-4c4-3%209-4%208-2v3c2%201%201%202-1%202-1%200-2%200-1%202l3%201c1%200%202%200%201-1l1-3c1-2%2010%205%2010%208-1%203%200%203%204-1l4-2v3c0%202%200%202%201%200l2-2%203-5%203-5-1-6c-2-6-2-7%200-10%205-5%209-17%209-25%200-4-1-4-4%200M25%20130c2%207%202%209%201%2010-1%200-3%203-4%208-1%208%200%2013%204%2015%203%202%202%203-3%202-9%200-5%202%206%203l10%203%209%202%2013%202c9%204%2042%201%2047-4l7-2c6%200%207-2%202-2-3%200-3%200-2-1%203-2%205-10%203-11l-1-3v-2c-1%200-2%201-2%204-1%205-1%206-5%206-2-1-3%200-3%201l-1%203-1-4c2-8%200-5-3%204-2%209-3%2010-3%202%200-3%200-5-1-4l-2%201h-1l-3-1c-3-2-6-2-7%201h-1c0-2-2-1-2%201l-2%201-2%201c0%201-1%201-1-1-1-3-1-3-1%201l-1%207c-1%201-3-1-4-6-1-3-1-4-2-3l-3%201-5%201c-4%200-11-2-13-6-2-2-3-2-2%202%200%202-1%202-10-1-8-2-11-9-7-13%205-5%205-12-1-18l-3-2v2\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
                "backgroundSize": "cover",
                "display": "block"
              }
            }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4be3c5f057137a44f2c170597e69be2c/ca0a1/Grumpy-cat-with-bugsica469.webp 223w", "/static/4be3c5f057137a44f2c170597e69be2c/75680/Grumpy-cat-with-bugsica469.webp 445w", "/static/4be3c5f057137a44f2c170597e69be2c/d95e5/Grumpy-cat-with-bugsica469.webp 639w"],
                "sizes": "(max-width: 639px) 100vw, 639px",
                "type": "image/webp"
              }}></source>{`
          `}<source parentName="picture" {...{
                "srcSet": ["/static/4be3c5f057137a44f2c170597e69be2c/853b2/Grumpy-cat-with-bugsica469.jpg 223w", "/static/4be3c5f057137a44f2c170597e69be2c/0a4fe/Grumpy-cat-with-bugsica469.jpg 445w", "/static/4be3c5f057137a44f2c170597e69be2c/df7e7/Grumpy-cat-with-bugsica469.jpg 639w"],
                "sizes": "(max-width: 639px) 100vw, 639px",
                "type": "image/jpeg"
              }}></source>{`
          `}<img parentName="picture" {...{
                "className": "gatsby-resp-image-image",
                "src": "/static/4be3c5f057137a44f2c170597e69be2c/df7e7/Grumpy-cat-with-bugsica469.jpg",
                "alt": "Grumpy cat with bugs",
                "title": "Grumpy cat with bugs",
                "loading": "lazy",
                "style": {
                  "width": "100%",
                  "height": "100%",
                  "margin": "0",
                  "verticalAlign": "middle",
                  "position": "absolute",
                  "top": "0",
                  "left": "0"
                }
              }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span>{`
    `}<figcaption parentName="figure" {...{
          "className": "gatsby-resp-image-figcaption"
        }}>{`Grumpy cat with bugs`}</figcaption>{`
  `}</figure></p>
    <h3 {...{
      "id": "ownership-takes-time"
    }}>{`Ownership takes time`}</h3>
    <p>{`As a professional software engineer, `}<strong parentName="p">{`you`}</strong>{` maintain this code. `}<em parentName="p">{`You`}</em>{` are the owner.`}</p>
    <p>{`When a bug happens in production, `}<em parentName="p">{`you`}</em>{` look into it. `}<em parentName="p">{`You`}</em>{` figure out who best can solve it. Could be you, could be an upstream or downstream system. `}<em parentName="p">{`You`}</em>{` ensure the bug gets fixed.`}</p>
    <p>{`That means `}<em parentName="p">{`you`}</em>{` are in charge of communicating with whomever stepped on the bug. Tell them you saw, loop them in when it's fixed, offer a workaround for right now.`}</p>
    <h3 {...{
      "id": "force-multiplying-takes-time"
    }}>{`Force multiplying takes time`}</h3>
    <p>{`As your knowledge of the system grows, your job shifts from that of writing the code to that of force-multiplying others.`}</p>
    <p><em parentName="p">{`You`}</em>{` can make others faster.`}</p>
    <p>{`When Susan hits a problem, she can spend 3 hours Googling for a solution, or 5 minutes asking you. When Joe can't understand a module, he can spend a day digging through the code, or 5 minutes asking you.`}</p>
    <p>{`20 minutes of your time to save 7 hours for the team.`}</p>
    <p>{`Your company will make that tradeoff every day. $40 of your expensive time for $420 of everybody else's time? Yes please.`}</p>
    <p>{`When you complain you can't get anything done 👇`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media4.giphy.com/media/1GT5PZLjMwYBW/giphy-loop.mp4?cid=7cf28b0fow2gr5n2mtajed5skrriyzam17fn0a4njzrht666&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p><strong parentName="p">{`Make sure your boss understands and values your force multiplier work!`}</strong></p>
    <p>{`I've been in situations where all this was expected, but only writing code was valued. That sucked.`}</p>
    <h2 {...{
      "id": "what-you-can-do-to-protect-your-coding-time"
    }}>{`What you can do to protect your coding time`}</h2>
    <p><em parentName="p">{`Do not become that ass who doesn't answer questions or help their team!`}</em></p>
    <p>{`You can use 2 broad strategies to protect your time:`}</p>
    <ol>
      <li parentName="ol">{`Timeboxing`}</li>
      <li parentName="ol">{`Optimization`}</li>
    </ol>
    <h2 {...{
      "id": "timeboxing"
    }}>{`Timeboxing`}</h2>
    <p><a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Timeboxing"
      }}>{`Timeboxing`}</a>{` is the strategy of blocking off your calendar for specific types of tasks.`}</p>
    <p>{`For example I have a meeting with myself every afternoon. 3pm to 6pm is coding time. My calendar appears blocked off and no meetings get scheduled.`}</p>
    <p>{`When you do have meetings, schedule them all in the same part of the day. Back to back.`}</p>
    <p>{`Nothing worse than a 15min stretch of time between 2 meetings. Too long to waste, too short to do anything.`}</p>
    <p>{`Same goes for Slack and email.`}</p>
    <p>{`Have a time in the day where you go through all of slack. Ignore notifications and questions for an hour, then answer everything in 15min.`}</p>
    <p>{`I find this hard to do but it's good advice to give. 😇`}</p>
    <p>{`Configure Slack so it never creates notifications and only dings when you are mentioned or DM'd. It's how you stay sane. Remove all other notifications. Keep your computer in DoNotDisturb mode.`}</p>
    <p>{`The fewer pings you get, the easier it is to ignore everything until your question-answering timebox.`}</p>
    <p><strong parentName="p">{`Do not expect immediate replies, do not give immediate replies. Let questions accumulate`}</strong></p>
    <h2 {...{
      "id": "optimization"
    }}>{`Optimization`}</h2>
    <p>{`How can you answer more questions faster?`}</p>
    <p>{`Documentation my friend.`}</p>
    <p>{`Nobody reads documentation do they? Out of date, hard to find, impossible to understand without context. Easier to ask.`}</p>
    <p>{`Here's what you do:`}</p>
    <ol>
      <li parentName="ol">{`Someone asks a question`}</li>
      <li parentName="ol">{`Answer`}</li>
      <li parentName="ol">{`Take 5min to write down your answer`}</li>
      <li parentName="ol">{`Share publicly`}</li>
    </ol>
    <p>{`Next time you get the same question, do this:`}</p>
    <ol>
      <li parentName="ol">{`Find your answer`}</li>
      <li parentName="ol">{`Spend 30sec validating it's correct`}</li>
      <li parentName="ol">{`Send link`}</li>
    </ol>
    <p>{`With time folks learn to check documentation first. If they don't, you're saving time by sharing pre-packaged answers.`}</p>
    <p>{`Saving answers to your questions helps you too. 6 months from now, you won't remember why or how you did something. Look it up in your notes. Make the notes searchable and shareable.`}</p>
    <h3 {...{
      "id": "ask-good-questions-and-help-others-ask-good-questions"
    }}>{`Ask good questions and help others ask good questions`}</h3>
    <p>{`Another optimization is asking good questions.`}</p>
    <p>{`Problem with a library or framework? Google it.`}</p>
    <p>{`Problem with `}<em parentName="p">{`our`}</em>{` code? Ask.`}</p>
    <p>{`Problem with how we're holding a library or framework? Ask.`}</p>
    <p>{`This is why you should avoid inventing everything from scratch and use public open source libraries. The more your team can learn from public resources, the less work for your seasoned engineers. 😉`}</p>
    <p>{`After that heuristic comes this magic question format:`}</p>
    <p><strong parentName="p">{`Can you help me with this problem? Who is the best person to ask? Here's what happens and this is what I expected to happen. I have tried X, Y, and Z to resolve the issue. I will be blocked by this in N minutes.`}</strong></p>
    <p>{`Ask before you're blocked, ask in public, present what you've tried, explain what happens and what you expected to happen.`}</p>
    <p>{`Any tips I've missed? hit reply`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: Once upon a time I wrote a book called `}<a parentName="p" {...{
        "href": "http://nightowlsbook.com/"
      }}>{`Why Programmers Work at Night`}</a>{`, which talks about how to be more productive as an engineer. You might enjoy it and I should re-read it too.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      